import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, Users, Eye, Globe, Calendar, MapPin, Flag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdmin } from '@/context/AdminContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country flag emoji helper
const getFlagEmoji = (countryCode) => {
  if (!countryCode) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

export default function AdminAnalytics() {
  const { token } = useAdmin();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/analytics/overview?token=${token}`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white" data-testid="admin-analytics-title">Analytics</h1>
        <p className="text-slate-400">Track your website performance and visitor insights</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Page Views</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {stats?.total_pageviews?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="bg-emerald-500 p-3 rounded-lg">
                  <Eye className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Views Today</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {stats?.pageviews_today?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="bg-blue-500 p-3 rounded-lg">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">This Week</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {stats?.pageviews_week?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="bg-purple-500 p-3 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Contacts</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {stats?.total_contacts?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="bg-amber-500 p-3 rounded-lg">
                  <Users className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Location Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Visitors by Country */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Flag className="w-5 h-5 text-emerald-500" />
              Visitors by Country
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.country_stats?.length > 0 ? (
              <div className="space-y-3">
                {stats.country_stats.map((country, index) => {
                  const maxCount = Math.max(...stats.country_stats.map(c => c.count));
                  const percentage = (country.count / maxCount) * 100;
                  
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-300 flex items-center gap-2">
                          <span className="text-lg">{getFlagEmoji(country.code)}</span>
                          {country.country}
                        </span>
                        <span className="text-white font-medium">{country.count}</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-emerald-500 h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Globe className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500">No location data yet</p>
                <p className="text-slate-600 text-sm">Location tracking starts with new visits</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Visitors by City */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-500" />
              Top Cities
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.city_stats?.length > 0 ? (
              <div className="space-y-3">
                {stats.city_stats.map((city, index) => {
                  const maxCount = Math.max(...stats.city_stats.map(c => c.count));
                  const percentage = (city.count / maxCount) * 100;
                  
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-300">
                          {city.city}{city.region ? `, ${city.region}` : ''}
                        </span>
                        <span className="text-white font-medium">{city.count}</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <MapPin className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500">No city data yet</p>
                <p className="text-slate-600 text-sm">City data will appear with new visits</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-500" />
              Top Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.page_stats?.length > 0 ? (
              <div className="space-y-4">
                {stats.page_stats.map((page, index) => {
                  const maxCount = Math.max(...stats.page_stats.map(p => p.count));
                  const percentage = (page.count / maxCount) * 100;
                  
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-300 truncate flex-1">{page.page || '/'}</span>
                        <span className="text-white font-medium ml-4">{page.count} views</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-purple-500 h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Globe className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500">No page view data yet</p>
                <p className="text-slate-600 text-sm">Visit your site to start tracking</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Trend */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-amber-500" />
              Daily Views (Last 14 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.daily_stats?.length > 0 ? (
              <div className="space-y-3">
                {stats.daily_stats.slice(-14).map((day, index) => {
                  const maxCount = Math.max(...stats.daily_stats.map(d => d.count));
                  const percentage = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                  
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-slate-500 text-xs w-20 flex-shrink-0">
                        {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      <div className="flex-1 bg-slate-700 rounded-full h-3">
                        <div 
                          className="bg-amber-500 h-3 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-white text-sm w-10 text-right">{day.count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500">No daily data yet</p>
                <p className="text-slate-600 text-sm">Check back after some activity</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Google Analytics Notice */}
      <Card className="bg-gradient-to-r from-blue-900/50 to-emerald-900/50 border-blue-700/50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="bg-blue-500 p-3 rounded-lg">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Google Analytics Connected</h3>
              <p className="text-slate-300 text-sm mt-1">
                Your site is also tracking with Google Analytics (G-YW020SM6YC). 
                For additional insights including device information, session duration, and more, 
                visit your Google Analytics dashboard.
              </p>
              <a 
                href="https://analytics.google.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-emerald-400 text-sm mt-3 hover:text-emerald-300"
              >
                Open Google Analytics
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
